/**
 * Risky Shipments Table
 *
 * Displays high-risk containers sorted by monetary value at risk.
 * Shows risk level, value, issues count, and financial impact.
 */
import { Link, useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { AlertTriangle, ChevronRight } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { useI18n } from '@/hooks/use-i18n';
import { useRiskyShipments, type RiskyShipment } from '@/hooks/api/use-risky-shipments';

interface RiskyShipmentsTableProps {
    /** Start of date range (ISO format: "2024-01-01") */
    startDate?: string | null;
    /** End of date range (ISO format: "2024-01-31") */
    endDate?: string | null;
    /** Maximum number of rows to display (default: 10) */
    maxCount?: number;
    /** Include medium risk containers (>= 0.25) in addition to high/critical */
    includeMedium?: boolean;
    /** Optional title override */
    title?: string;
}

// Helper function for risk level badge color
const getRiskLevelColor = (riskLevel: string): 'error' | 'warning' | 'success' | 'gray' => {
    switch (riskLevel.toLowerCase()) {
        case 'critical':
            return 'error';
        case 'high':
            return 'error';
        case 'medium':
            return 'warning';
        case 'low':
            return 'success';
        default:
            return 'gray';
    }
};

// Format currency value
const formatCurrency = (value: number | null, currency: string = 'USD'): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

// Format date
const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

export function RiskyShipmentsTable({
    startDate = null,
    endDate = null,
    maxCount = 10,
    includeMedium = false,
    title,
}: RiskyShipmentsTableProps) {
    const { t } = useTranslation(['pages:dashboard', 'pages:containers', 'common']);
    const { translateRiskLevel } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();

    const { data, isLoading, error } = useRiskyShipments(startDate, endDate, 0, maxCount, includeMedium, true);

    const displayTitle = title || t('controlHub.riskyShipments.title', { defaultValue: 'Risky Shipments' });
    const containers = data?.containers ?? [];

    // Empty state
    if (!isLoading && containers.length === 0) {
        return (
            <TableCard.Root>
                <TableCard.Header
                    title={displayTitle}
                    description={t('controlHub.riskyShipments.description', { defaultValue: 'High-risk containers sorted by value at risk' })}
                />
                <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="rounded-full bg-success-50 p-3 mb-4">
                        <AlertTriangle className="size-6 text-success-600" />
                    </div>
                    <p className="text-sm text-tertiary text-center">
                        {t('controlHub.riskyShipments.emptyState', { defaultValue: 'No high-risk shipments found' })}
                    </p>
                    <p className="text-xs text-quaternary text-center mt-1">
                        All shipments are within acceptable risk levels
                    </p>
                </div>
            </TableCard.Root>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <TableCard.Root>
                <TableCard.Header
                    title={displayTitle}
                    description={t('controlHub.riskyShipments.description', { defaultValue: 'High-risk containers sorted by value at risk' })}
                />
                <div className="flex items-center justify-center py-12">
                    <div className="animate-pulse flex items-center gap-2">
                        <div className="h-4 w-4 bg-tertiary/30 rounded-full" />
                        <div className="h-4 w-32 bg-tertiary/30 rounded" />
                    </div>
                </div>
            </TableCard.Root>
        );
    }

    // Error state
    if (error) {
        return (
            <TableCard.Root>
                <TableCard.Header
                    title={displayTitle}
                    description={t('controlHub.riskyShipments.description', { defaultValue: 'High-risk containers sorted by value at risk' })}
                />
                <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-sm text-error-600">Failed to load risky shipments</p>
                </div>
            </TableCard.Root>
        );
    }

    return (
        <TableCard.Root>
            <TableCard.Header
                title={displayTitle}
                description={t('controlHub.riskyShipments.description', { defaultValue: 'High-risk containers sorted by value at risk' })}
                contentTrailing={
                    data && data.total_count > maxCount && (
                        <Link
                            to={`/shipper/containers?risk=medium,high,critical${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}`}
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
                        >
                            View all ({data.total_count})
                            <ChevronRight className="size-4" />
                        </Link>
                    )
                }
            />
            <Table>
                <Table.Header>
                    <Table.Head label="Container Ref." isRowHeader />
                    <Table.Head label="Route" />
                    <Table.Head label="Risk" />
                    <Table.Head label="Value at Risk" />
                    <Table.Head label="Issues" />
                    <Table.Head label="Financial Impact" />
                    <Table.Head label="Alerts" />
                    <Table.Head label="ETA" />
                </Table.Header>
                <Table.Body>
                    {containers.map((shipment: RiskyShipment) => (
                        <Table.Row
                            key={shipment.container_id}
                            className="cursor-pointer hover:bg-secondary"
                            onClick={() => navigate(`/shipper/containers/${shipment.container_id}`, { state: navigationStateFrom(location) })}
                        >
                            {/* Container Number */}
                            <Table.Cell>
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="size-4 text-error-500" />
                                    <span className="font-medium text-primary">
                                        {shipment.container_number}
                                    </span>
                                </div>
                            </Table.Cell>

                            {/* Route */}
                            <Table.Cell>
                                <div className="text-sm text-tertiary">
                                    <span>{shipment.origin_country_code || '-'}</span>
                                    <span className="mx-1">→</span>
                                    <span>{shipment.destination_country_code || '-'}</span>
                                </div>
                                {(shipment.origin_port_name || shipment.destination_port_name) && (
                                    <div className="text-xs text-quaternary mt-0.5">
                                        {shipment.origin_port_name || '-'} → {shipment.destination_port_name || '-'}
                                    </div>
                                )}
                            </Table.Cell>

                            {/* Risk Level */}
                            <Table.Cell>
                                <div className="flex flex-col gap-1">
                                    <Badge color={getRiskLevelColor(shipment.risk_level)} size="sm">
                                        {translateRiskLevel(shipment.risk_level.charAt(0).toUpperCase() + shipment.risk_level.slice(1) as any)}
                                    </Badge>
                                    <span className="text-xs text-quaternary">
                                        Score: {(shipment.risk_score * 100).toFixed(0)}%
                                    </span>
                                </div>
                            </Table.Cell>

                            {/* Value at Risk */}
                            <Table.Cell>
                                <span className="font-medium text-primary">
                                    {formatCurrency(shipment.total_value_usd)}
                                </span>
                                {shipment.freight_charges_usd && (
                                    <div className="text-xs text-quaternary">
                                        Freight: {formatCurrency(shipment.freight_charges_usd)}
                                    </div>
                                )}
                            </Table.Cell>

                            {/* Issues Count */}
                            <Table.Cell>
                                <div className="flex items-center gap-2">
                                    <span className={`font-medium ${shipment.open_issues_count > 0 ? 'text-warning-600' : 'text-success-600'}`}>
                                        {shipment.open_issues_count}
                                    </span>
                                    {shipment.blocking_issues_count > 0 && (
                                        <Badge color="error" size="sm">
                                            {shipment.blocking_issues_count} blocking
                                        </Badge>
                                    )}
                                </div>
                            </Table.Cell>

                            {/* Financial Impact */}
                            <Table.Cell>
                                <span className={`text-sm ${shipment.total_financial_impact_usd ? 'text-error-600 font-medium' : 'text-tertiary'}`}>
                                    {shipment.total_financial_impact_usd
                                        ? formatCurrency(shipment.total_financial_impact_usd)
                                        : '-'
                                    }
                                </span>
                            </Table.Cell>

                            {/* Alerts */}
                            <Table.Cell>
                                {shipment.has_active_alerts ? (
                                    <Badge color="error" size="sm">
                                        {shipment.alert_count} active
                                    </Badge>
                                ) : (
                                    <span className="text-sm text-tertiary">None</span>
                                )}
                            </Table.Cell>

                            {/* ETA */}
                            <Table.Cell>
                                <span className="text-sm text-tertiary">
                                    {formatDate(shipment.estimated_arrival_date)}
                                </span>
                                {shipment.carrier_name && (
                                    <div className="text-xs text-quaternary">
                                        {shipment.carrier_name}
                                    </div>
                                )}
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </TableCard.Root>
    );
}
